/**
 * @file superAdminNavConfig.ts
 * @brief Navigation tree for the Super Admin mega menu.
 *
 * @author DES
 * @date 19-06-2026
 */

import {
  History,
  Bell,
  Briefcase,
  Building2,
  ChartColumn,
  Cpu,
  DollarSign,
  Earth,
  FileChartColumn,
  Image as ImageIcon,
  Link2,
  MessageSquareHeart,
  PenLine,
  Receipt,
  RefreshCw,
  ScrollText,
  ShieldCheck,
  Tags,
  Blocks,
  Users,
  Zap,
} from "lucide-react";
import { BoardroomTable } from "@/components/icons/BoardroomTable";
import { TodoTick } from "@/components/icons/TodoTick";
import { SessionTurns } from "@/components/icons/SessionTurns";
import { UsageRing } from "@/components/icons/UsageRing";
import { ADMIN_REPORT_CATALOG } from "@/features/admin-reports/reportCatalog";
import type { NavShellConfig } from "./nav-types";
import {
  BILLING_PATH,
  BILLING_SECTIONS,
  billingSectionHref,
} from "@/features/billing/billingNav";

const adminReportNavChildren = [
  {
    id: "reports-hub",
    to: "/admin/reports",
    label: "All reports",
    description: "Browse the full report catalog",
    icon: FileChartColumn,
    end: true as const,
  },
  ...ADMIN_REPORT_CATALOG.filter((r) => r.available).map((report) => ({
    id: `report-${report.id}`,
    to: `/admin/reports/${report.id}`,
    label: report.title,
    description: report.description,
    icon: report.icon,
  })),
];

export const superAdminNavConfig: NavShellConfig = {
  // Home, as for everyone else (DES 13-10-2026); Dashboard stays /admin.
  homeTo: "/admin/home",
  scopeTitle: "Super Admin",
  // The platform is the superadmin's company, as a company name is for
  // everyone else (DES 13-10-2026; was "Platform control plane").
  scopeSubtitle: "VERBLIZR",
  highlights: [
    {
      to: "/admin",
      label: "Dashboard",
      description: "Platform overview and cost alerts",
      icon: UsageRing, // DES 08-10-2026: app icon, was LayoutDashboard
      end: true,
    },
    {
      to: "/admin/sessions",
      label: "Sessions",
      description: "Cross-company session inspection",
      icon: SessionTurns, // DES 08-10-2026: app icon, was Activity
    },
    {
      to: "/admin/meetings",
      label: "Meeting Intel",
      description: "Platform-wide boardroom summaries & analytics",
      icon: BoardroomTable, // DES 08-10-2026: app icon, was Presentation
    },
    {
      to: "/admin/enterprise-inquiries",
      label: "Enterprise inquiries",
      description: "Plans → Enterprise contact-sales leads",
      icon: Briefcase,
    },
  ],
  groups: [
    {
      id: "directory",
      label: "Directory",
      items: [
        { to: "/admin/users", label: "Users", icon: Users },
        { to: "/admin/companies", label: "Companies", icon: Building2 },
        {
          to: "/admin/activity",
          label: "Activity log",
          description: "Deletes, member and access changes, across companies",
          icon: History,
        },
        {
          to: "/admin/enterprise-inquiries",
          label: "Enterprise inquiries",
          icon: Briefcase,
        },
      ],
    },
    {
      id: "operations",
      label: "Operations",
      items: [
        // DES 08-10-2026: app icon, was Activity
        { to: "/admin/sessions", label: "Sessions", icon: SessionTurns },
        /* The admin's OWN notes — personal scope, sitting in this nav for the
           same reason it does in the company one: notes belong to a person,
           not to a tenant, and a super admin is a person too. (23-08-2026) */
        {
          to: "/admin/notes",
          label: "Notes",
          description: "Write a meeting agenda · your own notes",
          icon: PenLine, // DES 08-10-2026: app icon, was NotebookPen
        },
        // The admin's OWN to-dos (DES 14-10-2026), as in the company and
        // employee navs; /admin/todos was already mounted (13-10-2026).
        {
          to: "/admin/todos",
          label: "My to-dos",
          description: "Open, overdue and done, from all your meetings",
          icon: TodoTick,
        },
        // DES 08-10-2026: app icon, was Presentation
        { to: "/admin/meetings", label: "Meeting Intel", icon: BoardroomTable },
        { to: "/admin/analytics", label: "Analytics", icon: ChartColumn },
        {
          to: "/admin/reports",
          label: "Reports",
          description: "Exportable ops and commercial tables",
          icon: FileChartColumn,
          children: adminReportNavChildren,
        },
        { to: "/admin/invoices", label: "Invoices", icon: Receipt },
        {
          to: BILLING_PATH,
          label: "Billing",
          icon: DollarSign,
          children: [
            {
              id: "billing-pricing",
              to: billingSectionHref(BILLING_SECTIONS.customerRates),
              label: "Customer rates",
              description: "Per-product pricing and platform tax",
              icon: Tags,
            },
            {
              id: "billing-alerts",
              to: billingSectionHref(BILLING_SECTIONS.costAlerts),
              label: "Cost alerts",
              description: "Daily spend threshold and notifications",
              icon: Bell,
            },
            {
              id: "billing-service-costs",
              to: billingSectionHref(BILLING_SECTIONS.serviceCosts),
              label: "Service costs",
              description: "What each vendor charges us",
              icon: Cpu,
            },
            {
              id: "billing-recompute",
              to: billingSectionHref(BILLING_SECTIONS.recompute),
              label: "Recompute costs",
              description: "Re-stamp historical session costs",
              icon: RefreshCw,
            },
            {
              id: "billing-enterprise-inquiries",
              to: "/admin/enterprise-inquiries",
              label: "Enterprise inquiries",
              description: "Contact-sales leads from Plans",
              icon: Briefcase,
            },
          ],
        },
        { to: "/admin/feedback", label: "Feedback", icon: MessageSquareHeart },
      ],
    },
    {
      id: "platform",
      label: "Platform",
      items: [
        {
          to: "/admin/modules",
          label: "Modules",
          description: "Platform-wide product availability",
          icon: Blocks,
        },
        /* How the product writes medico-legal reports — its own section, not
           a settings row: these are the standing rules the AI reads before
           every generation, and they are edited far more often than a
           provider picker. (14-09-2026) */
        {
          to: "/admin/report-instructions",
          label: "Report instructions",
          description: "Per-section rules the AI follows when writing reports",
          icon: ScrollText,
        },
        /* Zapier / Make on or off, platform and per company (B42 / W32, 30-10-2026). */
        {
          to: "/admin/integrations",
          label: "Integrations",
          description: "Zapier and Make for every company or one",
          icon: Zap,
        },
        { to: "/admin/languages", label: "Languages", icon: Earth },
        { to: "/admin/banners", label: "Home ads", icon: ImageIcon },
        { to: "/admin/settings", label: "Settings", icon: ShieldCheck },
        /* One source for links, contact and company details (B23, 24-10-2026). */
        {
          to: "/admin/link-settings",
          label: "Links & company info",
          description: "Store links, contact, company details, app versions",
          icon: Link2,
        },
      ],
    },
  ],
};
